import { useRef, type MouseEvent } from "react";
import { AlertDialog as AlertDialogRadix } from "radix-ui";
import type { IconName } from "../../icons";
import { icons } from "../../icons";
import cx from "../../../../lib/cx.js";
import buttonStyles from "../Button/Button.css";
import { text as textStyles } from "../Text/Text.css";
import dialogStyles from "../Dialog/Dialog.css";
import styles from "./AlertDialog.css";

const actionIconSize = "24px";

type AlertDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /**
   * `"default"` for a reversible or purely administrative confirmation
   * (accent action button); `"destructive"` for one that removes or revokes
   * access (solid error-red action button).
   */
  variant: "default" | "destructive";
  cancelLabel: string;
  actionLabel: string;
  actionIcon?: IconName;
  /** Disables the action button while the confirmed action is in flight. */
  isPending?: boolean;
  onAction: (event: MouseEvent<HTMLButtonElement>) => void;
};

// Radix's AlertDialog over its plain Dialog — a confirmation the user must
// explicitly accept or cancel, not dismiss by clicking outside or pressing
// Escape (Radix's AlertDialog already withholds the outside-click dismissal
// Dialog allows). Shares Dialog's shell styles (same overlay/content/header
// chrome) but a fixed two-button footer instead of an open slot, since the
// button pairing here is always exactly cancel + one variant-colored action.
// Styles Radix's own Title/Description/Cancel/Action directly with the
// Text/Button recipes rather than wrapping those components via `asChild` —
// neither forwards a ref, matching the pattern already established for
// Dialog and DropdownMenu.
const AlertDialog = ({
  open,
  onOpenChange,
  title,
  description,
  variant,
  cancelLabel,
  actionLabel,
  actionIcon,
  isPending,
  onAction,
}: AlertDialogProps) => {
  const ActionIcon = actionIcon ? icons[actionIcon] : null;
  const actionRef = useRef<HTMLButtonElement>(null);

  // Radix focuses Cancel on open — right for a destructive confirmation, where
  // an Enter press shouldn't remove anything by accident. A default one is
  // harmless to accept, so it focuses its action like the legacy Dialog does.
  const handleOpenAutoFocus = (event: Event) => {
    if (variant !== "default") return;
    event.preventDefault();
    actionRef.current?.focus({ preventScroll: true });
  };

  return (
    <AlertDialogRadix.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialogRadix.Portal>
        <AlertDialogRadix.Overlay className={dialogStyles.overlay} />
        <AlertDialogRadix.Content
          className={dialogStyles.content({ size: "medium" })}
          onOpenAutoFocus={handleOpenAutoFocus}
        >
          <div className={dialogStyles.header}>
            <AlertDialogRadix.Title
              className={textStyles({
                font: "subheader-small",
                color: "neutral-0",
              })}
            >
              {title}
            </AlertDialogRadix.Title>
          </div>

          <AlertDialogRadix.Description
            className={textStyles({ font: "body", color: "neutral-100" })}
          >
            {description}
          </AlertDialogRadix.Description>

          <div className={styles.footer}>
            <AlertDialogRadix.Cancel
              className={cx(
                buttonStyles.button({
                  size: "large",
                  color: "neutral",
                  variant: "secondary",
                }),
                styles.footerButton,
              )}
            >
              {cancelLabel}
            </AlertDialogRadix.Cancel>
            <AlertDialogRadix.Action
              ref={actionRef}
              className={cx(
                buttonStyles.button({
                  size: "large",
                  color: variant === "destructive" ? "error" : "accent",
                  variant: "primary",
                }),
                styles.footerButton,
              )}
              disabled={isPending}
              onClick={onAction}
            >
              {ActionIcon && <ActionIcon size={actionIconSize} aria-hidden />}
              {actionLabel}
            </AlertDialogRadix.Action>
          </div>
        </AlertDialogRadix.Content>
      </AlertDialogRadix.Portal>
    </AlertDialogRadix.Root>
  );
};

export default AlertDialog;
